import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlay, FaTrash, FaMusic, FaHeart, FaPlus, FaShare, FaTimes } from 'react-icons/fa';
import ShareModal from '../components/ShareModal';
import '../styles/PlaylistsPage.css';

function PlaylistsPage({ playTrack, addToQueue }) {
  const [playlists, setPlaylists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('playlists');
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDesc, setNewPlaylistDesc] = useState('');
  const [shareItem, setShareItem] = useState(null);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  useEffect(() => {
    loadPlaylists();
    loadFavorites();
  }, []);

  const loadPlaylists = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/playlists');
      setPlaylists(response.data);
    } catch (error) {
      console.error('Erreur chargement playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get('/api/playlists/favorites/list');
      setFavorites(response.data);
    } catch (error) {
      console.error('Erreur chargement favoris:', error);
    }
  };

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    try {
      await axios.post('/api/playlists', {
        name: newPlaylistName.trim(),
        description: newPlaylistDesc.trim() || null
      });
      setNewPlaylistName('');
      setNewPlaylistDesc('');
      setShowCreateForm(false);
      loadPlaylists();
    } catch (error) {
      console.error('Erreur création playlist:', error);
    }
  };

  const openPlaylist = async (playlistId) => {
    try {
      const response = await axios.get(`/api/playlists/${playlistId}`);
      setSelectedPlaylist(response.data);
    } catch (error) {
      console.error('Erreur ouverture playlist:', error);
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Supprimer cette playlist ?')) return;
    try {
      await axios.delete(`/api/playlists/${playlistId}`);
      setPlaylists(playlists.filter(p => p.id !== playlistId));
      if (selectedPlaylist?.id === playlistId) setSelectedPlaylist(null);
    } catch (error) {
      console.error('Erreur suppression playlist:', error);
    }
  };

  const updatePlaylist = async () => {
    if (!editName.trim() || !editingPlaylist) return;
    try {
      await axios.put(`/api/playlists/${editingPlaylist}`, {
        name: editName.trim(),
        description: editDesc.trim() || null
      });
      setEditingPlaylist(null);
      loadPlaylists();
      if (selectedPlaylist?.id === editingPlaylist) {
        setSelectedPlaylist(prev => ({ ...prev, name: editName.trim(), description: editDesc.trim() || null }));
      }
    } catch (error) {
      console.error('Erreur modification playlist:', error);
    }
  };

  const removeTrackFromPlaylist = async (playlistId, trackId) => {
    try {
      await axios.delete(`/api/playlists/${playlistId}/tracks/${trackId}`);
      setSelectedPlaylist(prev => ({
        ...prev,
        tracks: prev.tracks.filter(t => t.id !== trackId)
      }));
      loadPlaylists();
    } catch (error) {
      console.error('Erreur suppression piste:', error);
    }
  };

  const removeFavorite = async (albumId) => {
    try {
      await axios.delete(`/api/playlists/favorites/${albumId}`);
      setFavorites(favorites.filter(f => f.id !== albumId));
    } catch (error) {
      console.error('Erreur suppression favori:', error);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCoverUrl = (coverUrl) => {
    if (!coverUrl) return null;
    if (coverUrl.startsWith('http')) return coverUrl;
    const cleanPath = coverUrl.startsWith('/') ? coverUrl : `/${coverUrl}`;
    return `https://disney-music.gpworld.fr${cleanPath}`;
  };

  const playPlaylist = () => {
    if (selectedPlaylist && selectedPlaylist.tracks.length > 0) {
      playTrack(selectedPlaylist.tracks[0], selectedPlaylist.tracks);
    }
  };

  return (
    <div className="playlists-page">
      <div className="playlists-header">
        <h1>🎵 Ma Musique</h1>
        
        <div className="library-tabs">
          <button 
            className={`library-tab ${activeTab === 'playlists' ? 'active' : ''}`}
            onClick={() => setActiveTab('playlists')}
          >
            <FaMusic /> Playlists ({playlists.length})
          </button>
          <button 
            className={`library-tab ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            <FaHeart /> Favoris ({favorites.length})
          </button>
        </div>
      </div>

      <div className="library-content">
        {activeTab === 'playlists' && (
          <div className="playlists-section">
            {/* Bouton créer */}
            <div className="playlists-toolbar">
              {!showCreateForm ? (
                <button className="create-playlist-btn" onClick={() => setShowCreateForm(true)}>
                  <FaPlus /> Nouvelle playlist
                </button>
              ) : (
                <div className="create-playlist-form">
                  <input
                    type="text"
                    placeholder="Nom de la playlist..."
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createPlaylist()}
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="Description (optionnel)"
                    value={newPlaylistDesc}
                    onChange={(e) => setNewPlaylistDesc(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createPlaylist()}
                  />
                  <button className="btn-create" onClick={createPlaylist} disabled={!newPlaylistName.trim()}>Créer</button>
                  <button className="btn-cancel" onClick={() => { setShowCreateForm(false); setNewPlaylistName(''); setNewPlaylistDesc(''); }}>
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <p>Chargement...</p>
            ) : playlists.length === 0 ? (
              <div className="empty-state">
                <FaMusic size={60} />
                <h2>Aucune playlist</h2>
                <p>Créez des playlists et ajoutez-y des pistes depuis les albums !</p>
              </div>
            ) : (
              <div className="playlists-grid-page">
                {playlists.map(playlist => (
                  <div 
                    key={playlist.id} 
                    className="playlist-card"
                    onClick={() => openPlaylist(playlist.id)}
                  >
                    <div className="playlist-card-icon">🎵</div>
                    <div className="playlist-card-info">
                      <h3>{playlist.name}</h3>
                      {playlist.description && <p className="playlist-desc">{playlist.description}</p>}
                      <p className="playlist-count">{playlist.track_count || 0} piste(s)</p>
                    </div>
                    <div className="playlist-card-actions">
                      <button
                        className="playlist-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPlaylist(playlist.id);
                          setEditName(playlist.name);
                          setEditDesc(playlist.description || '');
                        }}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button 
                        className="playlist-delete"
                        onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id); }}
                        title="Supprimer"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="favorites-section">
            {favorites.length === 0 ? (
              <div className="empty-state">
                <FaHeart size={60} />
                <h2>Aucun favori</h2>
                <p>Ajoutez des albums à vos favoris !</p>
              </div>
            ) : (
              <div className="favorites-grid">
                {favorites.map(album => (
                  <div key={album.id} className="favorite-card">
                    <div className="favorite-cover">
                      {album.cover_url ? (
                        <img src={getCoverUrl(album.cover_url)} alt={album.title} />
                      ) : (
                        <div className="favorite-cover-placeholder">🎵</div>
                      )}
                    </div>
                    <div className="favorite-info">
                      <h3>{album.title}</h3>
                      <p>{album.artist}</p>
                    </div>
                    <button 
                      className="favorite-remove"
                      onClick={() => removeFavorite(album.id)}
                    >
                      <FaHeart />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modale édition playlist */}
      {editingPlaylist && (
        <div className="playlist-modal" onClick={() => setEditingPlaylist(null)}>
          <div className="playlist-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier la playlist</h3>
            <input
              type="text"
              placeholder="Nom"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
            />
            <input
              type="text"
              placeholder="Description"
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
            />
            <div className="edit-modal-actions">
              <button className="btn-create" onClick={updatePlaylist}>Enregistrer</button>
              <button className="btn-cancel" onClick={() => setEditingPlaylist(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Playlist Détails */}
      {selectedPlaylist && (
        <div className="playlist-modal" onClick={() => setSelectedPlaylist(null)}>
          <div className="playlist-modal-detail" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPlaylist(null)}>✕</button>
            
            <div className="playlist-detail-header">
              <div className="playlist-detail-icon">🎵</div>
              <div className="playlist-detail-info">
                <h2>{selectedPlaylist.name}</h2>
                {selectedPlaylist.description && <p className="playlist-detail-desc">{selectedPlaylist.description}</p>}
                <p>{selectedPlaylist.tracks?.length || 0} piste(s)</p>
                {selectedPlaylist.tracks?.length > 0 && (
                  <button className="play-all-btn" onClick={playPlaylist}>
                    <FaPlay /> Tout lire
                  </button>
                )}
              </div>
            </div>

            <div className="playlist-detail-tracks">
              {selectedPlaylist.tracks?.length === 0 ? (
                <p className="no-tracks">Cette playlist est vide</p>
              ) : (
                selectedPlaylist.tracks?.map((track, index) => {
                  const prevTrack = selectedPlaylist.tracks[index - 1];
                  const showDiscHeader = !prevTrack || prevTrack.disc_number !== track.disc_number;

                  return (
                    <React.Fragment key={track.id}>
                      {showDiscHeader && selectedPlaylist.tracks.some(t => t.disc_number > 1) && (
                        <div className="disc-header-detail">CD {track.disc_number}</div>
                      )}
                      
                      <div className="playlist-track-item">
                        <div 
                          className="playlist-track-play"
                          onClick={() => playTrack(track, selectedPlaylist.tracks)}
                        >
                          <FaPlay />
                        </div>
                        <div className="playlist-track-info">
                          <div className="playlist-track-title">{track.title}</div>
                          <div className="playlist-track-meta">
                            {track.artist} • {track.album_title}
                          </div>
                        </div>
                        <div className="playlist-track-duration">{formatTime(track.duration)}</div>
                        <button
                          className="playlist-track-action"
                          onClick={() => setShareItem({ type: 'track', trackId: track.id, title: track.title, artist: track.artist })}
                          title="Partager"
                        >
                          <FaShare />
                        </button>
                        <button
                          className="playlist-track-remove"
                          onClick={() => removeTrackFromPlaylist(selectedPlaylist.id, track.id)}
                          title="Retirer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {shareItem && <ShareModal item={shareItem} onClose={() => setShareItem(null)} />}
    </div>
  );
}

export default PlaylistsPage;
